/**
 * The signed-in learner's own certificates: the client for
 * `GET /api/certificates` (worker/certificates.ts) and the one copy of its
 * answer the course keeps. docs/certificates.md is the contract.
 *
 * ## One copy, for the page
 *
 * Two screens read this — the map's certificate strip and the clear overlay —
 * and the map remounts every time a learner comes back from a level. A fetch
 * per mount would ask the Worker the same question after every level, so the
 * answer is kept here for the life of the page and refetched only when
 * something can have changed it: a checkpoint pass, or a different account.
 *
 * ## Why a read is also how the course learns of an issue
 *
 * The route issues anything newly earned before it answers, so reading it
 * after a pass is not a poll of somebody else's work — it is the third of the
 * three issuing points, and the one that repairs an issue lost after the pass
 * itself. The overlay compares the answer before the pass with the answer
 * after it, and a certificate that moved to `issued` in between is the one
 * that was just earned.
 *
 * ## Read field by field
 *
 * The Worker can be a deploy ahead of or behind this bundle, so nothing here
 * trusts the shape: an unknown certificate id is dropped, an unknown state
 * reads as in progress, and a missing list is an empty one. A strip that
 * shows four certificates is recoverable; one that throws takes the map with
 * it.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { CERTIFICATES, type CertificateId } from '@engineer-os/engine';

export type CertificateState = 'issued' | 'revoked' | 'in-progress';

export interface CertificateProgress {
  certificate: CertificateId;
  /** Every checkpoint it needs, in course order. */
  missions: string[];
  /** The ones this account has passed, at any grading version. */
  passed: string[];
  state: CertificateState;
  /** Present once issued (and still present once revoked: the page says it is no longer valid). */
  id?: string;
  issuedAt?: number;
}

export interface Certificates {
  /** Whether the account owns the course. A free account sees progress and nothing issued. */
  owns: boolean;
  certificates: CertificateProgress[];
}

const KNOWN = new Set<string>(CERTIFICATES.map((c) => c.id));
const REQUEST_TIMEOUT = 15_000;

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** The Worker's answer, reduced to what this bundle understands. Exported for the tests. */
export function readCertificates(raw: unknown): Certificates {
  const body = (raw ?? {}) as { owns?: unknown; certificates?: unknown };
  const list = Array.isArray(body.certificates) ? body.certificates : [];
  const certificates: CertificateProgress[] = [];
  for (const item of list) {
    const c = (item ?? {}) as Record<string, unknown>;
    if (typeof c.certificate !== 'string' || !KNOWN.has(c.certificate)) continue;
    const state: CertificateState = c.state === 'issued' || c.state === 'revoked' ? c.state : 'in-progress';
    const missions = strings(c.missions);
    certificates.push({
      certificate: c.certificate as CertificateId,
      missions,
      passed: strings(c.passed).filter((m) => missions.includes(m)),
      // An issued or revoked certificate without an id has no page to link
      // to, and is drawn as progress rather than as a link to nowhere.
      state: state !== 'in-progress' && typeof c.id !== 'string' ? 'in-progress' : state,
      ...(typeof c.id === 'string' ? { id: c.id } : {}),
      ...(typeof c.issuedAt === 'number' ? { issuedAt: c.issuedAt } : {}),
    });
  }
  return { owns: body.owns === true, certificates };
}

export async function fetchCertificates(signal?: AbortSignal): Promise<Certificates> {
  const request = new AbortController();
  const abort = () => request.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(abort, REQUEST_TIMEOUT);
  try {
    const response = await fetch('/api/certificates', {
      credentials: 'same-origin', cache: 'no-store', signal: request.signal,
    });
    if (!response.ok) throw Object.assign(new Error('certificates request failed'), { status: response.status });
    return readCertificates(await response.json());
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

/* --- The page's copy ----------------------------------------------------- */

interface Held {
  /** Whose answer this is. A different account starts over. */
  user: string | null;
  value: Certificates | null;
  pending: Promise<Certificates | null> | null;
}

let held: Held = { user: null, value: null, pending: null };
const listeners = new Set<() => void>();

function publish(next: Held) {
  held = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Ask the Worker, and keep the answer. A failure keeps whatever was held
 * before it: a strip drawn from the last good answer is truer than one that
 * vanished because the network blinked.
 */
export function refreshCertificates(user: string): Promise<Certificates | null> {
  if (held.user !== user) publish({ user, value: null, pending: null });
  const pending = fetchCertificates()
    .then((value) => {
      if (held.user === user) publish({ user, value, pending: null });
      return value;
    })
    .catch(() => {
      if (held.user === user && held.pending === pending) publish({ ...held, pending: null });
      return null;
    });
  publish({ ...held, pending });
  return pending;
}

/** The held answer for this account, fetching it the first time anybody asks. */
export function loadCertificates(user: string): Promise<Certificates | null> {
  if (held.user === user) {
    if (held.pending) return held.pending;
    if (held.value) return Promise.resolve(held.value);
  }
  return refreshCertificates(user);
}

/** The held answer, for a render. Null while signed out, loading, or after a failure with nothing held. */
export function useCertificates(user: string | null): Certificates | null {
  const snapshot = useSyncExternalStore(subscribe, () => held, () => held);
  useEffect(() => {
    if (user) void loadCertificates(user);
  }, [user]);
  return user && snapshot.user === user ? snapshot.value : null;
}

/* --- Reading an answer --------------------------------------------------- */

/** Certificates that are issued in `after` and were not in `before`. */
export function newlyIssued(before: Certificates | null, after: Certificates): CertificateProgress[] {
  if (!before) return [];
  const was = new Map(before.certificates.map((c) => [c.certificate, c.state]));
  return after.certificates.filter((c) => c.state === 'issued' && c.id && was.get(c.certificate) !== 'issued');
}

/**
 * The milestone a free learner is closest to, for the offer: the one with the
 * most of its checkpoints passed as a share of what it needs, and the earlier
 * in the course on a tie. The whole-course certificate is left out while any
 * milestone has a pass, because "3 of 25" next to "3 of 6" is the smaller
 * truth said louder. Null when nothing has been passed at all.
 */
export function closestCertificate(value: Certificates): CertificateProgress | null {
  let best: CertificateProgress | null = null;
  for (const c of value.certificates) {
    if (c.passed.length === 0 || c.certificate === 'engineer') continue;
    if (!best || c.passed.length / c.missions.length > best.passed.length / best.missions.length) best = c;
  }
  return best;
}

/** The milestone certificate a checkpoint belongs to (never the whole-course one, which holds every checkpoint). */
export function milestoneFor(value: Certificates, missionId: string): CertificateProgress | null {
  return value.certificates.find((c) => c.certificate !== 'engineer' && c.missions.includes(missionId)) ?? null;
}

/* --- The moment of issue ------------------------------------------------- */

/**
 * What a checkpoint pass did to this account's certificates, for the clear
 * overlay: one or more issued, or one moved closer, or nothing worth a line.
 */
export type CertificateNews =
  | { kind: 'issued'; issued: { certificate: CertificateId; id: string }[] }
  | { kind: 'toward'; certificate: CertificateId; passed: number; total: number; owns: boolean };

/**
 * How far back an issue still counts as this pass's when there was no answer
 * from before the pass to compare with — the level was opened straight from a
 * link and the first read failed. Generous, because the other clock is the
 * Worker's; the cost of being wrong is announcing a certificate issued a few
 * minutes earlier, which is still news to somebody who has not seen it.
 */
const ISSUED_WITHIN_MS = 10 * 60_000;

export function certificateNews(
  before: Certificates | null, after: Certificates, missionId: string, now = Date.now(),
): CertificateNews | null {
  const issued = (before
    ? newlyIssued(before, after)
    : after.certificates.filter((c) => c.state === 'issued' && c.id && c.missions.includes(missionId)
      && c.issuedAt !== undefined && now - c.issuedAt < ISSUED_WITHIN_MS))
    .filter((c) => c.missions.includes(missionId));
  if (issued.length > 0) {
    return { kind: 'issued', issued: issued.map((c) => ({ certificate: c.certificate, id: c.id! })) };
  }
  const milestone = milestoneFor(after, missionId);
  // Already issued (a re-sit), or withdrawn with the course: this pass moved
  // nothing a learner would want told.
  if (!milestone || milestone.state !== 'in-progress') return null;
  // The pass that just landed counts even if the answer was read a moment
  // before it was written; the server's list is the rest.
  const passed = new Set([...milestone.passed, missionId]).size;
  return { kind: 'toward', certificate: milestone.certificate, passed, total: milestone.missions.length, owns: after.owns };
}
